import React, { useState, useEffect, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { UploadCloud, X, ExternalLink, Trash2, Copy, Check } from "lucide-react";
import { Button, Input } from "../../components/legacy-ui.js";
import {
  getBcvRate,
  getPaymentMethods,
  getHistoricalRates,
  createMilkProviderPayment,
  getPaymentEmitSources,
  createPaymentEmitSource,
} from "../../utils/api.js";
import { formatCurrency } from "../../utils/formatters.js";
import { filterPaymentMethodsByCurrency } from "../../utils/paymentUtils.js";
import {
  getBankById,
  paymentMethodRequiresBank,
  resolveBankImage,
  resolveBankLabel,
} from "../../utils/venezuelanBanks.js";
import BankSearchSelect from "../../components/BankSearchSelect.js";
import toast from "react-hot-toast";

const EMIT_FROM_LABEL = {
  personal: "Personal",
  juridico: "Jurídico",
  otra: "Otra",
};

/** Monto en Bs listo para pegar en apps bancarias (es-VE: 1.234,56). */
const formatBsForCopy = (value) =>
  Number(value).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatAmountForDisplay = (value) => {
  if (!value) return "";
  const parts = value.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const formatRate = (rate) => {
  if (!rate && rate !== 0) return "";
  return Number(rate).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formSelectClass =
  "w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none";

export default function MilkProviderPaymentForm({
  provider,
  weekStart,
  weekEnd,
  weekPayments = [],
  pendingAmount = 0,
  onSuccess,
  onDeletePayment,
}) {
  const [amount, setAmount] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [currency, setCurrency] = useState("BS");
  const [useBcv, setUseBcv] = useState(true);
  /** Si true (con tasa BCV), el usuario escribe en $ y se calcula Bs automáticamente. */
  const [enterInUsd, setEnterInUsd] = useState(false);
  const [copiedBs, setCopiedBs] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [bankId, setBankId] = useState("");
  const [emitFrom, setEmitFrom] = useState("");
  const [emitSourceSlug, setEmitSourceSlug] = useState("");
  const [emitSources, setEmitSources] = useState([]);
  const [addingEmitSource, setAddingEmitSource] = useState(false);
  const [newEmitSourceName, setNewEmitSourceName] = useState("");
  const [emitOtherName, setEmitOtherName] = useState("");
  const [savingEmitSource, setSavingEmitSource] = useState(false);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [filteredMethods, setFilteredMethods] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [currentBcvRate, setCurrentBcvRate] = useState(null);
  const [selectedRate, setSelectedRate] = useState("");
  const [historicalRates, setHistoricalRates] = useState([]);
  const [paymentDate, setPaymentDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  /** Fecha de la tasa BCV (igual que Reportes de pago) */
  const [rateDate, setRateDate] = useState("");

  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [bcvRes, methodsRes, histRes, emitRes] = await Promise.all([
          getBcvRate(),
          getPaymentMethods(),
          getHistoricalRates(),
          getPaymentEmitSources(),
        ]);
        setCurrentBcvRate(bcvRes.data?.data?.rate);
        setSelectedRate(bcvRes.data?.data?.rate || "");
        const methods = methodsRes.data?.data || [];
        setPaymentMethods(methods);
        if (methods.length > 0) setPaymentMethod(methods[0]._id);

        const sources = emitRes.data?.data || [];
        setEmitSources(sources);
        if (sources.length > 0) setEmitSourceSlug(sources[0].slug);

        const unique = (histRes.data?.usd || []).reduce((acc, current) => {
          if (
            !acc.find(
              (item) =>
                item.last_update === current.last_update && item.price === current.price,
            )
          ) {
            acc.push(current);
          }
          return acc;
        }, []);
        const sorted = unique.sort((a, b) => new Date(b.last_update) - new Date(a.last_update));
        setHistoricalRates(sorted);
        if (sorted.length > 0) {
          setRateDate(sorted[0].last_update.slice(0, 10));
        }
      } catch (e) {
        console.error(e);
        setError("No se pudieron cargar métodos de pago o tasas.");
      }
    };
    load();
  }, []);

  const ratesByDate = useMemo(() => {
    const map = {};
    historicalRates.forEach((rate) => {
      const date = rate.last_update.slice(0, 10);
      if (!map[date]) map[date] = rate.price;
    });
    return map;
  }, [historicalRates]);

  const availableDates = useMemo(
    () => Object.keys(ratesByDate).sort((a, b) => new Date(b) - new Date(a)),
    [ratesByDate],
  );

  useEffect(() => {
    if (currency === "BS") {
      setSelectedRate(ratesByDate[rateDate] || "");
    } else {
      setSelectedRate(currentBcvRate || "");
    }
  }, [currency, rateDate, ratesByDate, currentBcvRate]);

  useEffect(() => {
    const methods = filterPaymentMethodsByCurrency(paymentMethods, currency);
    setFilteredMethods(methods);
    if (!methods.some((m) => m._id === paymentMethod)) {
      setPaymentMethod(methods[0]?._id || "");
    }
  }, [currency, paymentMethods, paymentMethod]);

  const selectedMethodName = useMemo(() => {
    const method = paymentMethods.find((m) => m._id === paymentMethod);
    return method?.name || "";
  }, [paymentMethods, paymentMethod]);

  const needsBank =
    currency === "BS" && paymentMethodRequiresBank(selectedMethodName);

  useEffect(() => {
    if (!needsBank) {
      setBankId("");
      setEmitFrom("");
      setAddingEmitSource(false);
      setNewEmitSourceName("");
      setEmitOtherName("");
    }
  }, [needsBank]);

  useEffect(() => {
    if (emitFrom !== "juridico") {
      setAddingEmitSource(false);
      setNewEmitSourceName("");
    }
    if (emitFrom !== "otra") {
      setEmitOtherName("");
    }
  }, [emitFrom]);

  const handleEmitSourceSelect = (value) => {
    if (value === "__new__") {
      setAddingEmitSource(true);
      setEmitSourceSlug("");
      return;
    }
    setAddingEmitSource(false);
    setNewEmitSourceName("");
    setEmitSourceSlug(value);
  };

  const handleSaveEmitSource = async () => {
    const name = newEmitSourceName.trim();
    if (!name) {
      setError("Escribe el nombre de la cuenta jurídica.");
      return;
    }
    setSavingEmitSource(true);
    setError(null);
    try {
      const res = await createPaymentEmitSource({ name });
      const created = res.data?.data;
      if (!created?.slug) throw new Error("No se pudo crear");
      setEmitSources((prev) => {
        if (prev.some((item) => item.slug === created.slug)) return prev;
        return [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
      });
      setEmitSourceSlug(created.slug);
      setAddingEmitSource(false);
      setNewEmitSourceName("");
      toast.success("Cuenta agregada", { position: "top-center" });
    } catch (err) {
      const message =
        err.response?.data?.error || "No se pudo agregar la cuenta jurídica.";
      setError(message);
      toast.error(message, { position: "top-center" });
    } finally {
      setSavingEmitSource(false);
    }
  };

  const suggestAmount = useCallback(
    (curr, pending, rate) => {
      if (!pending || pending <= 0) return "";
      if (curr === "USD") return Number(pending.toFixed(2)).toString();
      if (curr === "BS" && rate) return Number((pending * rate).toFixed(2)).toString();
      return "";
    },
    [],
  );

  useEffect(() => {
    if (!provider) return;
    const rate = currency === "BS" ? ratesByDate[rateDate] : null;
    setAmount(suggestAmount(currency, pendingAmount, rate));
    setReference("");
    setNotes("");
    setReceiptFile(null);
    setReceiptPreview("");
    setError(null);
    // Solo al cambiar proveedor o pendiente; moneda/fecha se manejan en sus handlers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id, pendingAmount]);

  useEffect(() => {
    if (currency !== "BS" || !useBcv || enterInUsd) return;
    const rate = ratesByDate[rateDate] || selectedRate;
    if (rate && pendingAmount > 0) {
      setAmount(suggestAmount("BS", pendingAmount, rate));
    }
  }, [currency, useBcv, enterInUsd, rateDate, ratesByDate, selectedRate, pendingAmount, suggestAmount]);

  const applyUsdToBs = (usdRaw, rate) => {
    const usd = parseFloat(usdRaw) || 0;
    if (!rate || usd <= 0) {
      setAmount("");
      return;
    }
    setAmount(Number((usd * rate).toFixed(2)).toString());
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    if (newCurrency !== "BS") {
      setUseBcv(true);
      setEnterInUsd(false);
    }
    const rate =
      newCurrency === "BS" && useBcv
        ? ratesByDate[rateDate] || selectedRate
        : null;
    if (newCurrency === "BS" && !useBcv) {
      setEnterInUsd(false);
      setAmount("");
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    } else if (newCurrency === "BS" && enterInUsd) {
      const usd =
        pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "";
      setAmountUsd(usd);
      applyUsdToBs(usd, rate);
    } else {
      setAmount(suggestAmount(newCurrency, pendingAmount, rate));
      setAmountUsd("");
    }
  };

  const handleUseBcvChange = (checked) => {
    setUseBcv(checked);
    if (checked) {
      const rate = ratesByDate[rateDate] || selectedRate;
      if (enterInUsd) {
        const usd =
          amountUsd ||
          (pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
        setAmountUsd(usd);
        applyUsdToBs(usd, rate);
      } else {
        setAmount(suggestAmount("BS", pendingAmount, rate));
        setAmountUsd("");
      }
    } else {
      setEnterInUsd(false);
      setAmount("");
      setAmountUsd(pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "");
    }
  };

  const handleEnterInUsdChange = (checked) => {
    setEnterInUsd(checked);
    const rate = ratesByDate[rateDate] || selectedRate;
    if (checked) {
      const usd =
        pendingAmount > 0 ? Number(pendingAmount.toFixed(2)).toString() : "";
      setAmountUsd(usd);
      applyUsdToBs(usd, rate);
    } else {
      setAmountUsd("");
      setAmount(suggestAmount("BS", pendingAmount, rate));
    }
  };

  const handleRateDateChange = (newDate) => {
    setRateDate(newDate);
    if (currency === "BS" && useBcv) {
      const rate = ratesByDate[newDate];
      if (enterInUsd) {
        applyUsdToBs(amountUsd, rate);
      } else if (rate && pendingAmount > 0) {
        setAmount(Number((pendingAmount * rate).toFixed(2)).toString());
      }
    }
  };

  const handlePaymentDateChange = (newDate) => {
    setPaymentDate(newDate);
    if (currency === "BS" && useBcv && ratesByDate[newDate]) {
      handleRateDateChange(newDate);
    }
  };

  const handleAmountChange = (e) => {
    let raw = e.target.value.replace(/,/g, "");
    if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
      raw = raw.replace(/\./g, "");
    }
    if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setAmount(raw);
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    if (file.type.startsWith("image/")) {
      setReceiptPreview(URL.createObjectURL(file));
    } else {
      setReceiptPreview("");
    }
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview("");
  };

  const handleAmountUsdChange = (e) => {
    let raw = e.target.value.replace(/,/g, "");
    if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
      raw = raw.replace(/\./g, "");
    }
    if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
      setAmountUsd(raw);
      if (currency === "BS" && useBcv && enterInUsd) {
        applyUsdToBs(raw, ratesByDate[rateDate] || selectedRate);
      }
    }
  };

  const numericAmount = parseFloat(amount) || 0;
  const numericAmountUsd = parseFloat(amountUsd) || 0;
  const rateNum = parseFloat(selectedRate) || 0;
  const manualRate =
    currency === "BS" && !useBcv && numericAmount > 0 && numericAmountUsd > 0
      ? numericAmount / numericAmountUsd
      : 0;

  const handleCopyBsAmount = async () => {
    if (!(numericAmount > 0)) return;
    const text = formatBsForCopy(numericAmount);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedBs(true);
      toast.success("Monto en Bs copiado", { position: "top-center" });
      setTimeout(() => setCopiedBs(false), 2000);
    } catch {
      toast.error("No se pudo copiar", { position: "top-center" });
    }
  };

  const amountInUSD = useMemo(() => {
    if (currency === "USD") return numericAmount || null;
    if (currency === "BS" && (!useBcv || enterInUsd)) return numericAmountUsd || null;
    if (currency === "BS" && rateNum > 0 && numericAmount) return numericAmount / rateNum;
    return null;
  }, [numericAmount, numericAmountUsd, currency, rateNum, useBcv, enterInUsd]);

  let remainingUSD;
  if (provider && numericAmount > 0 && amountInUSD !== null) {
    remainingUSD = pendingAmount - amountInUSD;
  }

  const pendingInBs =
    currency === "BS" && rateNum > 0 ? pendingAmount * rateNum : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!provider || !numericAmount) return;
    if (currency === "BS" && useBcv && !selectedRate) {
      setError("No hay tasa de cambio disponible para la fecha seleccionada.");
      return;
    }
    if (currency === "BS" && (!useBcv || enterInUsd) && !(numericAmountUsd > 0)) {
      setError("Indica el monto en dólares.");
      return;
    }
    if (needsBank && !bankId) {
      setError("Selecciona el banco donde realizaste el pago.");
      return;
    }
    if (needsBank && !emitFrom) {
      setError("Indica desde dónde emitiste el pago.");
      return;
    }
    if (needsBank && emitFrom === "juridico") {
      if (addingEmitSource) {
        setError("Guarda la nueva cuenta jurídica o elige una existente.");
        return;
      }
      if (!emitSourceSlug) {
        setError("Selecciona la cuenta jurídica desde la que emitiste.");
        return;
      }
    }
    if (needsBank && emitFrom === "otra" && !emitOtherName.trim()) {
      setError("Indica desde dónde se emitió el pago.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("provider", provider.id);
      formData.append("amount", String(numericAmount));
      formData.append("currency", currency);
      formData.append("paymentMethod", paymentMethod);
      formData.append("reference", reference);
      formData.append("notes", notes);
      formData.append("weekStart", weekStart);
      formData.append("weekEnd", weekEnd);
      // Fecha elegida + hora exacta del momento del registro
      const now = new Date();
      const dayKey = paymentDate || format(now, "yyyy-MM-dd");
      const [year, month, day] = dayKey.split("-").map(Number);
      const paymentDateTime = new Date(
        year,
        month - 1,
        day,
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds(),
      );
      formData.append("paymentDate", paymentDateTime.toISOString());
      if (needsBank && bankId) {
        const bank = getBankById(bankId);
        if (bank) {
          formData.append(
            "bank",
            JSON.stringify({ id: bank.id, name: bank.name, code: bank.code }),
          );
        }
      }
      if (needsBank && emitFrom) {
        formData.append("emitFrom", emitFrom);
        if (emitFrom === "juridico" && emitSourceSlug) {
          const source = emitSources.find((item) => item.slug === emitSourceSlug);
          formData.append(
            "emitSource",
            JSON.stringify({
              slug: emitSourceSlug,
              name: source?.name || emitSourceSlug,
            }),
          );
        } else if (emitFrom === "otra") {
          formData.append(
            "emitSource",
            JSON.stringify({ name: emitOtherName.trim() }),
          );
        }
      }

      if (currency === "BS") {
        if (useBcv) {
          formData.append("exchangeRate", String(selectedRate));
          formData.append("rateDate", rateDate);
        } else {
          formData.append("exchangeRate", String(numericAmount / numericAmountUsd));
        }
      }

      if (receiptFile) formData.append("receipt", receiptFile);

      await createMilkProviderPayment(formData);
      setAmount("");
      setBankId("");
      setEmitFrom("");
      setAddingEmitSource(false);
      setNewEmitSourceName("");
      setEmitOtherName("");
      clearReceipt();
      toast.success("Abono registrado", { position: "top-center" });
      onSuccess?.();
    } catch (err) {
      const message = err.response?.data?.error || "Error al registrar el pago.";
      setError(message);
      toast.error(message, { position: "top-center" });
    } finally {
      setSubmitting(false);
    }
  };

  const getPayUsd = (pay) =>
    pay.amountInUSD ?? pay.conversionDetails?.amountInUSD ?? pay.amount;

  const getMethodName = (pay) => pay.paymentMethod?.name || pay.method || "—";
  const getBankName = (pay) => resolveBankLabel(pay.bank) || "";
  const getEmitLabel = (pay) => {
    if (!pay?.emitFrom) return "";
    if (pay.emitFrom === "juridico") {
      const detail = pay.emitSource?.name;
      return detail
        ? `Jurídico · ${detail}`
        : EMIT_FROM_LABEL.juridico;
    }
    if (pay.emitFrom === "otra") {
      const detail = pay.emitSource?.name;
      return detail ? `Otra · ${detail}` : EMIT_FROM_LABEL.otra;
    }
    return EMIT_FROM_LABEL[pay.emitFrom] || pay.emitFrom;
  };

  if (!provider) {
    return (
      <p className="text-gray-400 text-center py-8 text-sm">
        Seleccione un proveedor de la lista para registrar un pago.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-100 p-3 rounded-lg text-sm space-y-1">
          <p>
            Proveedor: <strong>{provider.name}</strong>
          </p>
          <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-4">
            <span>
              Deuda semana: <strong className="text-emerald-700">{formatCurrency(provider.totalOwed)}</strong>
            </span>
            <span className="text-red-600">
              Pendiente: <strong>{formatCurrency(pendingAmount)}</strong>
              {currency === "BS" && useBcv && pendingInBs != null && (
                <span className="block text-xs font-normal text-red-500/90 mt-0.5">
                  ≈ {formatCurrency(pendingInBs, "BS")} a tasa del día
                </span>
              )}
            </span>
          </div>
          {weekStart && weekEnd && (
            <p className="text-xs text-gray-500">
              {format(new Date(weekStart), "d MMM", { locale: es })} –{" "}
              {format(new Date(weekEnd), "d MMM yyyy", { locale: es })}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Moneda</label>
          <select
            className={formSelectClass}
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
          >
            <option value="BS">Bolívares (Bs)</option>
            <option value="USD">Dólares ($)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Fecha del abono</label>
          <input
            type="date"
            required
            className={formSelectClass}
            value={paymentDate}
            onChange={(e) => handlePaymentDateChange(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Puedes poner cualquier fecha. El abono queda en esta semana.
          </p>
        </div>

        {currency === "BS" && (
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-emerald-700 focus:ring-emerald-600"
                checked={useBcv}
                onChange={(e) => handleUseBcvChange(e.target.checked)}
              />
              Usar tasa BCV
            </label>
            {useBcv && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-emerald-700 focus:ring-emerald-600"
                  checked={enterInUsd}
                  onChange={(e) => handleEnterInUsdChange(e.target.checked)}
                />
                Ingresar monto en $
              </label>
            )}
          </div>
        )}

        {currency === "BS" && useBcv && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fecha de la Tasa</label>
              <input
                type="date"
                className={formSelectClass}
                value={rateDate}
                onChange={(e) => handleRateDateChange(e.target.value)}
                min={availableDates.length > 0 ? availableDates[availableDates.length - 1] : ""}
                max={availableDates.length > 0 ? availableDates[0] : ""}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tasa de Cambio (Bs/USD)</label>
              <input
                type="text"
                readOnly
                className={`${formSelectClass} bg-gray-100 font-semibold text-gray-800`}
                value={selectedRate ? formatRate(selectedRate) : ""}
                placeholder="Sin tasa para esta fecha"
              />
              {!selectedRate && rateDate && (
                <p className="text-xs text-amber-600 mt-1">No hay tasa BCV registrada para esta fecha.</p>
              )}
            </div>
          </div>
        )}

        {currency === "BS" && useBcv && enterInUsd ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Monto en $</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                required
                className={formSelectClass}
                value={formatAmountForDisplay(amountUsd)}
                onChange={handleAmountUsdChange}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Monto en Bs (según tasa)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  className={`${formSelectClass} bg-gray-100 font-semibold text-gray-800 flex-1`}
                  value={numericAmount > 0 ? formatAmountForDisplay(amount) : ""}
                  placeholder="Se calcula automáticamente"
                />
                <button
                  type="button"
                  onClick={handleCopyBsAmount}
                  disabled={!(numericAmount > 0)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Copiar monto en Bs"
                >
                  {copiedBs ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                  {copiedBs ? "Copiado" : "Copiar"}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Este es el monto que se registra y puedes pegarlo en la transferencia.
              </p>
            </div>
            {numericAmount > 0 && remainingUSD !== undefined && (
              <p className="text-xs text-gray-600">
                Pendiente restante: <strong className="font-mono">{formatCurrency(remainingUSD)}</strong>
                {rateNum > 0 && (
                  <span className="text-gray-500"> ({formatCurrency(remainingUSD * rateNum, "BS")})</span>
                )}
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Monto a Pagar ({currency === "BS" ? "Bs" : "USD"})
            </label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              required
              className={formSelectClass}
              value={formatAmountForDisplay(amount)}
              onChange={handleAmountChange}
            />
            {currency === "BS" && useBcv && amountInUSD !== null && numericAmount > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Equivalente a: <strong>{formatCurrency(amountInUSD, "USD")}</strong>
              </p>
            )}
            {numericAmount > 0 && remainingUSD !== undefined && (
              <p className="text-xs text-gray-600 mt-1">
                Pendiente restante: <strong className="font-mono">{formatCurrency(remainingUSD)}</strong>
                {currency === "BS" && useBcv && rateNum > 0 && (
                  <span className="text-gray-500"> ({formatCurrency(remainingUSD * rateNum, "BS")})</span>
                )}
              </p>
            )}
          </div>
        )}

        {currency === "BS" && !useBcv && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Monto $</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                required
                className={formSelectClass}
                value={formatAmountForDisplay(amountUsd)}
                onChange={handleAmountUsdChange}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tasa usada (Bs/$)</label>
              <input
                type="text"
                readOnly
                className={`${formSelectClass} bg-gray-100 font-semibold text-gray-800`}
                value={manualRate > 0 ? formatRate(manualRate) : ""}
                placeholder="Se calcula con Bs y $"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Método de Pago</label>
          <select
            className={formSelectClass}
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            required
          >
            <option value="" disabled>
              Seleccione un método
            </option>
            {filteredMethods.map((method) => (
              <option key={method._id} value={method._id}>
                {method.name}
              </option>
            ))}
          </select>
        </div>

        {needsBank && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Banco *
              </label>
              <BankSearchSelect
                value={bankId}
                onChange={setBankId}
                required
                placeholder="Buscar banco donde pagaste…"
              />
              <p className="text-xs text-gray-500 mt-1">
                Indica desde qué banco hiciste el pago móvil o la transferencia.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Emitido desde *
              </label>
              <select
                className={formSelectClass}
                value={emitFrom}
                onChange={(e) => setEmitFrom(e.target.value)}
                required
              >
                <option value="" disabled>
                  Personal, jurídico u otra
                </option>
                <option value="personal">Personal</option>
                <option value="juridico">Jurídico</option>
                <option value="otra">Otra…</option>
              </select>
            </div>

            {emitFrom === "otra" && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  ¿Desde dónde? *
                </label>
                <input
                  type="text"
                  className={formSelectClass}
                  value={emitOtherName}
                  onChange={(e) => setEmitOtherName(e.target.value)}
                  placeholder="Ej. Cuenta de un socio, caja chica…"
                  required
                  autoFocus
                />
              </div>
            )}

            {emitFrom === "juridico" && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Cuenta jurídica *
                </label>
                {!addingEmitSource ? (
                  <>
                    <select
                      className={formSelectClass}
                      value={emitSourceSlug}
                      onChange={(e) => handleEmitSourceSelect(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        Selecciona la cuenta
                      </option>
                      {emitSources.map((source) => (
                        <option key={source.slug} value={source.slug}>
                          {source.name}
                        </option>
                      ))}
                      <option value="__new__">+ Agregar otra…</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Puedes agregar otra cuenta para usarla en próximos abonos.
                    </p>
                  </>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      className={formSelectClass}
                      value={newEmitSourceName}
                      onChange={(e) => setNewEmitSourceName(e.target.value)}
                      placeholder="Ej. Inversiones Lactium RR26, C.A"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={handleSaveEmitSource}
                        disabled={savingEmitSource || !newEmitSourceName.trim()}
                        className="flex-1"
                      >
                        {savingEmitSource ? "Guardando…" : "Guardar cuenta"}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setAddingEmitSource(false);
                          setNewEmitSourceName("");
                          if (!emitSourceSlug && emitSources[0]) {
                            setEmitSourceSlug(emitSources[0].slug);
                          }
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <Input
          label="Referencia"
          type="text"
          placeholder="Nro. de confirmación, etc."
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Notas (opcional)</label>
          <textarea
            rows={2}
            className={formSelectClass}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observaciones del pago"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Comprobante (opcional)</label>
          {!receiptFile ? (
            <label
              htmlFor="milk-receipt-upload"
              className="cursor-pointer flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50"
            >
              <UploadCloud size={22} className="mb-1" />
              <span className="text-xs">Foto o PDF del comprobante</span>
              <input
                id="milk-receipt-upload"
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={handleReceiptChange}
              />
            </label>
          ) : (
            <div className="relative border border-gray-200 rounded-lg p-2 bg-gray-50">
              <button
                type="button"
                onClick={clearReceipt}
                className="absolute top-1 right-1 p-1 text-gray-500 hover:text-red-600 rounded"
                aria-label="Quitar comprobante"
              >
                <X size={16} />
              </button>
              {receiptPreview ? (
                <img src={receiptPreview} alt="Vista previa" className="max-h-32 mx-auto rounded object-contain" />
              ) : (
                <p className="text-sm text-gray-600 text-center py-4 pr-6">{receiptFile.name}</p>
              )}
            </div>
          )}
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}

        <Button
          type="submit"
          className="w-full"
          disabled={
            !amount ||
            submitting ||
            (needsBank && !bankId) ||
            (needsBank && !emitFrom) ||
            (needsBank && emitFrom === "juridico" && (addingEmitSource || !emitSourceSlug)) ||
            (needsBank && emitFrom === "otra" && !emitOtherName.trim()) ||
            (currency === "BS" && useBcv && !selectedRate) ||
            (currency === "BS" && (!useBcv || enterInUsd) && !amountUsd)
          }
        >
          {submitting ? "Registrando…" : "Registrar"}
        </Button>
      </form>

      {weekPayments.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Pagos de esta semana
          </h4>
          <ul className="space-y-2 max-h-48 overflow-y-auto">
            {weekPayments.map((pay) => (
              <li
                key={pay._id}
                className="flex justify-between items-start gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2 border border-gray-100"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-800">
                    {pay.currency === "BS"
                      ? formatCurrency(pay.amount, "BS")
                      : formatCurrency(getPayUsd(pay), "USD")}
                    {pay.currency === "BS" && (
                      <span className="text-xs font-normal text-gray-500 ml-1">
                        ({formatCurrency(getPayUsd(pay), "USD")})
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {getMethodName(pay)}
                    {getBankName(pay) ? ` · ${getBankName(pay)}` : ""}
                    {getEmitLabel(pay) ? ` · ${getEmitLabel(pay)}` : ""}
                    {pay.reference ? ` · ${pay.reference}` : ""}
                  </p>
                  {resolveBankImage(pay.bank) ? (
                    <img
                      src={resolveBankImage(pay.bank)}
                      alt=""
                      className="h-5 w-5 mt-1 rounded object-contain"
                    />
                  ) : null}
                  {pay.currency === "BS" && pay.conversionDetails?.exchangeRate && (
                    <p className="text-[10px] text-gray-400">
                      Tasa: Bs. {formatRate(pay.conversionDetails.exchangeRate)}/USD
                      {pay.conversionDetails.rateDate
                        ? ` · ${format(new Date(`${pay.conversionDetails.rateDate}T12:00:00`), "dd/MM/yyyy")}`
                        : ""}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-400">
                    {format(new Date(pay.paymentDate || pay.createdAt), "dd/MM/yyyy hh:mm a")}
                  </p>
                  {pay.receiptUrl && (
                    <a
                      href={pay.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 mt-1 hover:underline"
                    >
                      <ExternalLink size={12} /> Ver comprobante
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onDeletePayment?.(pay._id)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded shrink-0"
                  title="Eliminar pago"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
